import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, X, Check } from 'lucide-react';

const ConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Konfirmasi',
  message = 'Apakah Anda yakin?',
  confirmText = 'Ya',
  cancelText = 'Batal',
  type = 'danger', // 'danger' | 'warning' | 'info'
  icon
}) => {
  const dialogRef = useRef(null);

  useEffect(() => {
    if (isOpen && dialogRef.current) {
      dialogRef.current.focus();
    }

    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const getTypeConfig = () => {
    switch (type) {
      case 'danger':
        return {
          iconBg: 'bg-red-500/20',
          iconColor: 'text-red-500',
          confirmBg: 'bg-red-500 hover:bg-red-600',
          borderColor: 'border-red-500/30'
        };
      case 'warning':
        return {
          iconBg: 'bg-yellow-500/20',
          iconColor: 'text-yellow-500',
          confirmBg: 'bg-yellow-500 hover:bg-yellow-600 text-black',
          borderColor: 'border-yellow-500/30'
        };
      case 'info':
        return {
          iconBg: 'bg-blue-500/20',
          iconColor: 'text-blue-500',
          confirmBg: 'bg-blue-500 hover:bg-blue-600',
          borderColor: 'border-blue-500/30'
        };
      default:
        return {
          iconBg: 'bg-[#D4AF7A]/20',
          iconColor: 'text-[#D4AF7A]',
          confirmBg: 'bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] hover:opacity-90 text-black',
          borderColor: 'border-[#D4AF7A]/30'
        };
    }
  };

  const config = getTypeConfig();

  const content = (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Dialog */}
      <div
        ref={dialogRef}
        className="relative bg-[#0A0A0A] border rounded-2xl max-w-md w-full p-6 shadow-2xl animate-in fade-in zoom-in duration-200"
        style={{
          borderColor: `var(--tw-color-opacity, ${config.borderColor})`,
        }}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-[#F7F5F2]/60 hover:text-[#F7F5F2] hover:bg-white/10 rounded-lg transition-colors"
          aria-label="Close"
        >
          <X size={18} />
        </button>

        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className={`p-3 rounded-full ${config.iconBg}`}>
            {icon || <AlertTriangle size={32} className={config.iconColor} />}
          </div>
        </div>

        {/* Content */}
        <div className="text-center mb-6">
          <h3 className="text-xl font-bold text-[#F7F5F2] mb-2">
            {title}
          </h3>
          <p className="text-[#F7F5F2]/80">
            {message}
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 text-[#F7F5F2] rounded-lg hover:bg-white/10 transition-colors font-medium"
          >
            {cancelText}
          </button>
          <button
            onClick={() => {
              onConfirm();
              onClose();
            }}
            className={`flex-1 px-4 py-2.5 ${config.confirmBg} rounded-lg transition-all font-medium flex items-center justify-center gap-2`}
          >
            <Check size={18} />
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

// Hook for easier usage
import { useState, useCallback } from 'react';

export const useConfirm = () => {
  const [dialog, setDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    type: 'danger',
  });

  const confirm = useCallback((
    message,
    options = {}
  ) => {
    return new Promise((resolve) => {
      setDialog({
        isOpen: true,
        title: options.title || 'Konfirmasi',
        message,
        onConfirm: () => resolve(true),
        type: options.type || 'danger',
        confirmText: options.confirmText || 'Ya',
        cancelText: options.cancelText || 'Batal',
        icon: options.icon,
      });
    });
  }, []);

  const handleClose = useCallback(() => {
    setDialog((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const ConfirmDialogComponent = useCallback(() => (
    <ConfirmDialog
      isOpen={dialog.isOpen}
      onClose={handleClose}
      onConfirm={dialog.onConfirm}
      title={dialog.title}
      message={dialog.message}
      type={dialog.type}
      confirmText={dialog.confirmText}
      cancelText={dialog.cancelText}
      icon={dialog.icon}
    />
  ), [dialog, handleClose]);

  return { confirm, ConfirmDialog: ConfirmDialogComponent };
};

export default ConfirmDialog;
